import { useState } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { useSearch } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import {
  downloadExport,
  useBilling,
  useCheckoutSession,
  usePortalSession,
  type BillingState,
} from '@/data/billing';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { FleetBillingStatus } from '@breaker/shared-types';

const STATUS_COPY: Record<FleetBillingStatus, { label: string; tone: string }> = {
  comped: { label: 'Included', tone: 'bg-breaker-ground-2 text-breaker-ink-2' },
  trialing: { label: 'Trial', tone: 'bg-breaker-amber/10 text-breaker-amber-deep' },
  active: { label: 'Active', tone: 'bg-emerald-50 text-emerald-700' },
  past_due: { label: 'Payment failed', tone: 'bg-breaker-amber/10 text-breaker-amber-deep' },
  suspended: { label: 'Paused', tone: 'bg-breaker-red/10 text-breaker-red' },
};

function pounds(pence: number): string {
  return `£${(pence / 100) % 1 === 0 ? pence / 100 : (pence / 100).toFixed(2)}`;
}

/** "£20 per driver / month". Prices are quoted inc-VAT (locked ruling
 *  2026-08-15): the number shown is the number on the card statement — no
 *  "+ VAT" is ever appended (Stripe carries the inclusive tax behaviour). */
export function priceLine(state: BillingState): string {
  return `${pounds(state.pricePerSeatPence)} per driver / month`;
}

export function BillingPage() {
  const { fleet, role } = useActiveFleet();
  const billingQuery = useBilling(fleet?.id ?? null);
  const checkout = useCheckoutSession(fleet?.id ?? null);
  const portal = usePortalSession(fleet?.id ?? null);
  const search = useSearch({ strict: false }) as { checkout?: string };
  // Unit D: the CSV export locks while the download is being prepared
  // (replaces the old exporting state).
  const exportLock = useActionLock();
  const [exportError, setExportError] = useState<string | null>(null);

  if (!fleet || billingQuery.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }
  const state = billingQuery.data;
  if (!state) {
    return (
      <div className="px-8 py-10 max-w-[720px]">
        <p className="text-sm text-breaker-mute">
          Billing isn&apos;t visible for your role in this fleet.
        </p>
      </div>
    );
  }

  const { billing } = state;
  const isOwner = role === 'owner';
  // 0084: an exempt fleet's underlying status is still honest ('trialing'
  // for a comp-code signup), so the label comes from the exemption, not the
  // lifecycle — otherwise a founding fleet reads "Trial" and then, after 30
  // days, keeps reading "Trial" with no countdown and no explanation.
  const status = billing.exempt
    ? { label: 'Founding', tone: 'bg-breaker-identity/10 text-breaker-identity' }
    : STATUS_COPY[billing.status];

  function handleExport() {
    if (!fleet) return;
    setExportError(null);
    exportLock.run(() =>
      downloadExport(fleet.id).catch(() => {
        setExportError('Export failed — try again in a minute.');
      }),
    );
  }

  return (
    <div className="px-8 py-10 max-w-[720px]">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">Billing</h1>
        <p className="text-sm text-breaker-mute mt-1">
          {billing.exempt
            ? 'One plan, everything included — no charge on your account.'
            : `One plan, everything included — ${priceLine(state)}.`}
        </p>
      </header>

      {search.checkout === 'success' && (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Card added — you&apos;re all set. This page updates as Stripe confirms.
        </div>
      )}
      {search.checkout === 'cancelled' && (
        <div className="mb-6 rounded-lg border border-breaker-line bg-breaker-ground-2 px-4 py-3 text-sm text-breaker-ink-2">
          Checkout was cancelled — no changes made.
        </div>
      )}

      <section className="rounded-xl border border-breaker-line bg-white p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.tone}`}>
                {status.label}
              </span>
              {billing.exempt && (
                <span className="text-sm text-breaker-ink-2">no charge</span>
              )}
              {!billing.exempt && billing.status === 'trialing' && billing.trialDaysLeft !== null && (
                <span className="text-sm text-breaker-ink-2">
                  {billing.trialDaysLeft} day{billing.trialDaysLeft === 1 ? '' : 's'} left
                </span>
              )}
              {billing.status === 'past_due' && billing.graceEndsAt && (
                <span className="text-sm text-breaker-amber-deep">
                  update your card before{' '}
                  {new Date(billing.graceEndsAt).toLocaleDateString('en-GB')} to keep access
                </span>
              )}
            </div>
            <p className="text-sm text-breaker-mute mt-2">
              {billing.exempt ? (
                <>
                  {state.seats} driver{state.seats === 1 ? '' : 's'} — not charged. We still
                  count seats so your numbers are right; nothing is ever billed.
                </>
              ) : (
                <>
                  {state.seats} driver{state.seats === 1 ? '' : 's'} billed — minimum one
                  driver. Invited and suspended drivers are free.
                </>
              )}
            </p>
          </div>
          <div className="text-right">
            {billing.exempt ? (
              <>
                <div className="text-2xl font-semibold text-breaker-identity">
                  Founding
                </div>
                <div className="text-xs text-breaker-mute">no charge</div>
              </>
            ) : (
              <>
                <div className="text-2xl font-semibold text-breaker-ink tabular-nums">
                  {pounds(state.seats * state.pricePerSeatPence)}
                </div>
                <div className="text-xs text-breaker-mute">per month</div>
              </>
            )}
          </div>
        </div>

        {billing.status === 'suspended' && (
          <p className="text-sm text-breaker-ink-2 border-t border-breaker-line pt-4">
            Your subscription is paused, so dispatch is read-only. Nothing has been
            deleted — your customer portal keeps serving existing jobs, and you can
            export everything below. Restart any time.
          </p>
        )}

        {isOwner && state.available && (
          <div className="flex gap-3 border-t border-breaker-line pt-4">
            {state.hasSubscription || state.hasCard ? (
              <Button action={() => portal.mutateAsync().catch(() => undefined)}>
                {billing.status === 'past_due' || billing.status === 'suspended'
                  ? 'Fix payment'
                  : 'Manage billing'}
              </Button>
            ) : (
              <Button action={() => checkout.mutateAsync().catch(() => undefined)}>
                Add a card
              </Button>
            )}
            {(checkout.isError || portal.isError) && (
              <span className="text-sm text-breaker-red self-center">
                That didn&apos;t work — try again.
              </span>
            )}
          </div>
        )}
        {billing.exempt && (
          <p className="text-sm text-breaker-mute border-t border-breaker-line pt-4">
            You&rsquo;re a founding fleet — everything is switched on and there&rsquo;s
            nothing to pay. No card, no trial to run out.
          </p>
        )}
        {isOwner && !state.available && !billing.exempt && billing.status !== 'comped' && (
          <p className="text-sm text-breaker-mute border-t border-breaker-line pt-4">
            Card management isn&apos;t available right now — try again shortly.
          </p>
        )}
      </section>

      <section className="mt-6 rounded-xl border border-breaker-line bg-white p-6">
        <h2 className="text-sm font-medium text-breaker-ink">Your data</h2>
        <p className="text-sm text-breaker-mute mt-1">
          Download your loads, customers and invoices as CSV — available in every
          state, including paused.
        </p>
        {isOwner && (
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExport}
              loading={exportLock.locked}
            >
              {exportLock.locked ? 'Preparing…' : 'Download CSV bundle'}
            </Button>
            {exportError && <span className="text-sm text-breaker-red">{exportError}</span>}
          </div>
        )}
      </section>
    </div>
  );
}
